import { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { BackendApi } from "../api/api.ts";
import { User } from "../api/accessControl/User.ts";
import { BackendAPITypes } from "../generated/blueprint.ts";
import type { Subledger } from "../pages/app/partner/pages/partnerDashboardPage/components/Subledgers/types.ts";

export interface UseSubledgersResult {
    subledgers: Subledger[];
    isLoading: boolean;
    create: (name: string) => Promise<Subledger>;
    rename: (subledgerId: string, name: string) => Promise<Subledger>;
    refetch: () => void;
}

export function useSubledgers(user: User, businessId: string | null): UseSubledgersResult {
    const [subledgers, setSubledgers] = useState<Subledger[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshCount, setRefreshCount] = useState(0);
    const token = user.getToken();

    useEffect(() => {
        if (!businessId) {
            setSubledgers([]);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        const api = new BackendApi(token);

        async function fetchSubledgers() {
            try {
                const response: BackendAPITypes.BACKENDPreHandleSyncApiV1PartnerGetClientSubledgersGetResponseBody =
                    await api.Partner__getClientSubledgers(businessId!);
                if (cancelled) return;
                setSubledgers(response.subledgers);
            } catch (error) {
                Sentry.captureException(error);
                if (!cancelled) setSubledgers([]);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }

        fetchSubledgers();
        return () => {
            cancelled = true;
        };
    }, [token, businessId, refreshCount]);

    const refetch = useCallback(() => setRefreshCount((count) => count + 1), []);

    const create = useCallback(
        async (name: string): Promise<Subledger> => {
            if (!businessId) {
                throw new Error("No client selected");
            }
            const api = new BackendApi(token);
            const created = await api.Partner__createClientSubledger({ business_id: businessId, name: name.trim() });
            refetch();
            return created;
        },
        [token, businessId, refetch],
    );

    const rename = useCallback(
        async (subledgerId: string, name: string): Promise<Subledger> => {
            if (!businessId) {
                throw new Error("No client selected");
            }
            const api = new BackendApi(token);
            const renamed = await api.Partner__renameClientSubledger({
                business_id: businessId,
                subledger_id: subledgerId,
                name: name.trim(),
            });
            refetch();
            return renamed;
        },
        [token, businessId, refetch],
    );

    return { subledgers, isLoading, create, rename, refetch };
}
